import React from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Separator from "@radix-ui/react-separator";
import TagPill from "../components/dossier/TagPill";
import SessionAccordion from "../components/dossier/SessionAccordion";
import { buildDossierPath, DOSSIER_MODES, isExpressDossierMode } from "../data/dossierModes";
import DossierEmptyState from "../components/dossier/DossierEmptyState";
import PDFPreview, { assetURL } from "../components/dossier/PDFPreview";
import { getDossierCatalog } from "../data/dossierCatalog";

/* ------------------------------ Utils robustos ------------------------------ */
// 1) Elimina variation selectors (ej. '▪︎' = '▪' + U+FE0F)
const removeVS = (s = "") => String(s).replace(/\uFE0F/g, "");

// 2) Limpia bullets / guiones iniciales (• ● ▪ ▫ ○ ◦ · * y – — -)
const cleanLine = (s = "") =>
  removeVS(s)
    .replace(/^\s*(?:[•●▪▫○◦·*]|[-–—])\s*/u, "")
    .trim();

// 3) Convierte string con líneas o array a array limpio
const toArray = (val) => {
  if (Array.isArray(val)) return val.map(cleanLine).filter(Boolean);
  if (typeof val === "string")
    return val
      .replace(/\r/g, "")
      .split("\n")
      .map(cleanLine)
      .filter(Boolean);
  return [];
};

/** Extrae {items, role} desde:
 *  - objeto { passos, rol_docent }
 *  - string con bullets y un "🎓 Rol del docent: ..." embebido */
const parsePrepareSection = (section) => {
  // caso objeto
  if (section && typeof section === "object" && !Array.isArray(section)) {
    const items = toArray(section.passos || section.items || []);
    const role =
      typeof section.rol_docent === "string" ? section.rol_docent : "";
    return { items, role };
  }
  // caso string (bloque |)
  if (typeof section === "string") {
    const idx = section.indexOf("🎓");
    if (idx >= 0) {
      const before = section.slice(0, idx);
      const after = section.slice(idx);
      const items = toArray(before);
      const role = after
        .replace(/^🎓\s*Rol del docent:\s*/i, "")
        .replace(/\n\s*/g, " ")
        .trim();
      return { items, role };
    }
    return { items: toArray(section), role: "" };
  }
  return { items: [], role: "" };
};

/** Grupo de pills (continguts/competències) */
const PillGroup = ({ items, seed = "session" }) => {
  const list = toArray(items);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((label, i) => (
        <TagPill key={`${label}-${i}`} label={label} seed={seed} index={i} />
      ))}
    </div>
  );
};


/** Tarjeta contenedor (menos padding y radios más contenidos) */
const Card = ({ title, children, className = "" }) => (
  <section
    className={`bg-white/85 backdrop-blur rounded-2xl border p-5 md:p-6 space-y-3 ${className}`}
  >
    {title && <h3 className="text-lg md:text-xl font-semibold">{title}</h3>}
    {children}
  </section>
);

/** Lista con marcador personalizado (por defecto punto) */
const BulletList = ({ items, marker = "•", markerClass = "text-gray-400" }) => {
  const list = toArray(items);
  if (!list.length) return null;
  return (
    <ul className="space-y-1.5 leading-relaxed">
      {list.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span
            className={`select-none mt-[3px] inline-block min-w-[1em] text-center ${markerClass}`}
          >
            {marker}
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
};

const ExpressResourceCard = ({ item }) => {
  const { t } = useTranslation();

  return (
    <article className="rounded-2xl border bg-white overflow-hidden hover:shadow-lg transition">
      <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        <PDFPreview src={assetURL(item.path)} title={item.label} />
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-emerald-600 text-white">
          PDF
        </span>
      </div>
      <div className="p-4">
        <div className="text-sm font-medium leading-snug">{item.label}</div>
        <a
          className="mt-2 inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-white hover:bg-gray-50 transition"
          href={assetURL(item.path)}
          download
        >
          {t("dossier_didactic.download", "Descarregar")}
        </a>
      </div>
    </article>
  );
};

/* --------------------------------- Página --------------------------------- */
export default function SessionDetailPage({ mode = DOSSIER_MODES.STANDARD }) {
  const { sessionId } = useParams();
  const { t, i18n } = useTranslation();
  const materialLocale = i18n.resolvedLanguage || i18n.language;

  if (isExpressDossierMode(mode)) {
    const session = (getDossierCatalog(mode, materialLocale).sessions || []).find(
      (candidate) => candidate.id === String(Number(sessionId))
    );

    if (!session) {
      return <DossierEmptyState />;
    }

    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h2
            className="font-extrabold tracking-tight"
            style={{ fontSize: "clamp(28px, 4.5vw, 40px)", lineHeight: 1.08 }}
          >
            {session.title}
          </h2>
          <p className="text-sm text-gray-700">
            {t("ui.dossier.expressTeacherMaterials", "Material professorat")}
          </p>
        </div>

        <Separator.Root className="bg-gray-200 h-px w-full" />

        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6">
          {(session.resources || []).map((resource) => (
            <ExpressResourceCard key={resource.path} item={resource} />
          ))}
        </div>

        <div className="pt-1">
          <Link to={buildDossierPath("/dossier/sessions", mode)} className="underline">
            {t("ui.dossier.links.backSessions", "← Tornar a sessions")}
          </Link>
        </div>
      </div>
    );
  }

  const base = `dossier_didactic.sessions.${sessionId}`;

  // Cabecera
  const guia = t(`${base}.guia`, { defaultValue: "" });
  const titol = t(`${base}.titol_apartat`, { defaultValue: "" });
  const descripcio = t(`${base}.descripcio`, { defaultValue: "" });

  // Blocs + competències (admite array o string)
  const continguts = t(`${base}.blocs_i_competencies.continguts`, {
    returnObjects: true,
    defaultValue: [],
  });
  const competencies = t(`${base}.blocs_i_competencies.competencies`, {
    returnObjects: true,
    defaultValue: [],
  });

  // Sidebar (admite array o string)
  const material = t(`${base}.material`, {
    returnObjects: true,
    defaultValue: [],
  });
  const recursos = t(`${base}.recursos`, {
    returnObjects: true,
    defaultValue: [],
  }); // opcional

  // Preparem — cada sección puede ser objeto {passos,rol_docent} o string (|)
  const rawEng = t(`${base}.preparem.enganxem`, {
    returnObjects: true,
    defaultValue: "",
  });
  const rawExp = t(`${base}.preparem.explorem`, {
    returnObjects: true,
    defaultValue: "",
  });
  const rawExi = t(`${base}.preparem.expliquem`, {
    returnObjects: true,
    defaultValue: "",
  });
  const rawEla = t(`${base}.preparem.elaborem`, {
    returnObjects: true,
    defaultValue: "",
  });
  const rawAva = t(`${base}.preparem.avaluem`, {
    returnObjects: true,
    defaultValue: "",
  });

  const eng = parsePrepareSection(rawEng);
  const exp = parsePrepareSection(rawExp);
  const exi = parsePrepareSection(rawExi);
  const ela = parsePrepareSection(rawEla);
  const ava = parsePrepareSection(rawAva);

  // Criteris (array o string)
  const criteris = t(`${base}.criteris_avaluacio`, {
    returnObjects: true,
    defaultValue: [],
  });

  const notFound = !guia && !titol && !descripcio;
  if (notFound) {
    return (
      <div className="rounded-2xl border bg-white p-6">
        <p className="text-red-600">
          {t("ui.dossier.errors.sessionNotFound", "Sessió no trobada.")}
        </p>
        <Link to={buildDossierPath("/dossier/sessions", mode)} className="underline mt-2 inline-block">
          {t("ui.dossier.links.backSessions", "← Tornar a sessions")}
        </Link>
      </div>
    );
  }

  const hasPrepare = [eng, exp, exi, ela, ava].some(
    (s) => s.items?.length || s.role
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        {!!guia && (
          <h2
            className="font-extrabold tracking-tight"
            style={{ fontSize: "clamp(28px, 4.5vw, 40px)", lineHeight: 1.08 }}
          >
            {guia}
          </h2>
        )}
        {!!titol && <p className="text-sm text-gray-700">{titol}</p>}
      </div>

      {/* GRUPO SUPERIOR: Descripció + Materials (juntos) */}
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Descripció (columna ancha y texto más concentrado) */}
        {!!descripcio && (
          <Card className="lg:col-span-8">
            <div className="space-y-3 text-[15px] leading-[1.75]">
              <p className="whitespace-pre-line">{descripcio}</p>
            </div>
          </Card>
        )}

        {/* Materials (columna lateral con marcadores modernos) */}
        {!!toArray(material).length && (
          <Card
            className={`lg:col-span-4 ${!descripcio ? "lg:col-start-1" : ""}`}
            title={t("ui.common.materials", "Materials")}
          >
            <BulletList
              items={material}
              marker="▸"
              markerClass="text-emerald-600"
            />
          </Card>
        )}
      </div>

      {/* Bloques de contenido + competencias (pegado al grupo superior) */}
      {(toArray(continguts).length || toArray(competencies).length) && (
        <Card title={t("ui.common.content_and_competencies", "Blocs de contingut i competències")}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">
                {t("ui.common.contents", "Continguts")}
              </h4>
              <PillGroup items={continguts} />
            </div>
            <div>
              <h4 className="font-semibold mb-2">
                {t("ui.common.competencies", "Competències")}
              </h4>
              <PillGroup items={competencies} />
            </div>
          </div>
        </Card>
      )}

      <Separator.Root className="bg-gray-200 h-px w-full" />

      {/* Preparem (desplegables cerrados por defecto) */}
      {hasPrepare && (
        <div className="space-y-3">
          <h3 className="text-lg md:text-xl font-semibold">
            {t("ui.common.prepare", "Preparem")}
          </h3>
          <SessionAccordion
            labels={{
              hook: t("ui.prepare.blocks.enganxem", "Enganxem"),
              explore: t("ui.prepare.blocks.explorem", "Explorem"),
              explain: t("ui.prepare.blocks.expliquem", "Expliquem"),
              elaborate: t("ui.prepare.blocks.elaborem", "Elaborem"),
              assess: t("ui.prepare.blocks.avaluem", "Avaluem"),
            }}
            prepare={{
              enganxem: { items: eng.items, role: eng.role },
              explorem: { items: exp.items, role: exp.role },
              expliquem: { items: exi.items, role: exi.role },
              elaborem: { items: ela.items, role: ela.role },
              avaluem: { items: ava.items, role: ava.role },
            }}
          />
        </div>
      )}

      {/* Criteris d’avaluació */}
      {!!toArray(criteris).length && (
        <Card title={t("ui.common.evaluation", "Criteris d’avaluació")}>
          <BulletList items={criteris} />
        </Card>
      )}

      {/* Volver */}
      <div className="pt-1">
        <Link to={buildDossierPath("/dossier/sessions", mode)} className="underline">
          {t("ui.dossier.links.backSessions", "← Tornar a sessions")}
        </Link>
      </div>
    </div>
  );
}
